import { Text } from '@chakra-ui/react';
import { useMemo } from 'react';

interface MessageHighlighterProps {
  text: string;
  searchKeyword: string;
  maxLength?: number;
  highlightColor?: string;
}

const MessageHighlighter = ({
  text = '',
  searchKeyword = '',
  maxLength = 25,
  highlightColor = '',
}: MessageHighlighterProps) => {
  const processedText = useMemo(() => {
    if (!searchKeyword || !text) {
      return { displayText: text, isTruncated: false };
    }

    const lowerText = text.toLowerCase();
    const lowerKeyword = searchKeyword.toLowerCase();
    const keywordIndex = lowerText.indexOf(lowerKeyword);

    if (keywordIndex === -1) {
      return { displayText: text, isTruncated: false };
    }

    const keywordLength = searchKeyword.length;
    if (keywordIndex === 0 || keywordIndex + keywordLength <= maxLength) {
      if (text.length <= maxLength) {
        return {
          displayText: highlightKeyword(text, searchKeyword, highlightColor),
          isTruncated: false,
        };
      }
      const truncated = text.substring(0, maxLength) + '...';
      return {
        displayText: highlightKeyword(truncated, searchKeyword, highlightColor),
        isTruncated: true,
      };
    }

    const keywordEndIndex = keywordIndex + keywordLength;
    if (keywordEndIndex >= text.length) {
      const start = Math.max(0, text.length - maxLength);
      const truncated = (start > 0 ? '...' : '') + text.substring(start);
      return {
        displayText: highlightKeyword(truncated, searchKeyword, highlightColor),
        isTruncated: start > 0,
      };
    }
    return handleMiddleKeyword(
      text,
      searchKeyword,
      keywordIndex,
      keywordLength,
      maxLength,
      highlightColor
    );
  }, [text, searchKeyword, maxLength, highlightColor]);

  return (
    <Text
      fontSize="xs"
      fontWeight="medium"
      color="fg.muted"
      dangerouslySetInnerHTML={{ __html: processedText.displayText }}
      lineClamp={1}
    />
  );
};

const handleMiddleKeyword = (
  text: string,
  searchKeyword: string,
  keywordIndex: number,
  keywordLength: number,
  maxLength: number,
  highlightColor: string
) => {
  const charsLeft = Math.floor(maxLength / 2);
  const charsRight = maxLength - charsLeft - 3;
  let start = keywordIndex - charsLeft;
  let end = keywordIndex + keywordLength + charsRight;
  if (start < 0) {
    end += Math.abs(start);
    start = 0;
  }
  if (end > text.length) {
    start -= end - text.length;
    start = Math.max(0, start);
    end = text.length;
  }
  let displayText = '';
  if (start > 0) {
    displayText += '...';
  }
  displayText += text.substring(start, end);
  if (end < text.length) {
    displayText += '...';
  }
  return {
    displayText: highlightKeyword(displayText, searchKeyword, highlightColor),
    isTruncated: start > 0 || end < text.length,
  };
};

const highlightKeyword = (text: string, keyword: string, highlightColor: string) => {
  if (!keyword) return escapeHtml(text);
  const escapedKeyword = escapeRegex(keyword);
  const regex = new RegExp(`(${escapedKeyword})`, 'gi');
  return escapeHtml(text).replace(
    regex,
    `<span style="color: ${highlightColor}; font-weight: bold;">$1</span>`
  );
};

const escapeHtml = (text: string) => {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
};

const escapeRegex = (string: string) => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

export default MessageHighlighter;
